import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import './ProductForm.css';

const SECTIONS = [
  { key: 'general', icon: '📦' },
  { key: 'pricing', icon: '💰' },
  { key: 'physical', icon: '📐' },
];

const defaultValues = {
  name: '',
  sku: '',
  reference: '',
  barcode: '',
  productType: 'goods',
  categoryId: '',
  supplierId: '',
  brandId: '',
  unitOfMeasure: 'unit',
  description: '',
  salesPrice: '',
  cost: '',
  quantityOnHand: '',
  canBeSold: true,
  trackInventory: true,
  isFavorite: false,
  weight: '',
  volume: '',
  customerLeadTime: '',
  responsible: '',
  descriptionReceipts: '',
  descriptionDelivery: '',
  internalNotes: '',
};

export default function ProductForm({
  initialData,
  categories = [],
  suppliers = [],
  brands = [],
  onSave,
  onCancel,
  saving = false,
  error = null,
}) {
  const { t } = useTranslation();
  const [data, setData] = useState(defaultValues);
  const [activeSection, setActiveSection] = useState('general');
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (initialData) {
      setData({
        ...defaultValues,
        ...initialData,
        categoryId: initialData.categoryId ?? '',
        supplierId: initialData.supplierId ?? '',
        brandId: initialData.brandId ?? '',
        salesPrice: initialData.salesPrice ?? '',
        cost: initialData.cost ?? '',
        quantityOnHand: initialData.quantityOnHand ?? '',
        weight: initialData.weight ?? '',
        volume: initialData.volume ?? '',
        customerLeadTime: initialData.customerLeadTime ?? '',
      });
    } else {
      setData(defaultValues);
    }
    setTouched(false);
    setActiveSection('general');
  }, [initialData]);

  const updateField = useCallback((key, value) => {
    setData(prev => ({ ...prev, [key]: value }));
  }, []);

  const nameIsValid = data.name.trim().length > 0;
  const canSave = nameIsValid && !saving;

  const handleSave = () => {
    setTouched(true);
    if (!nameIsValid) return;

    const payload = {
      ...data,
      salesPrice: data.salesPrice === '' ? null : parseFloat(data.salesPrice),
      cost: data.cost === '' ? null : parseFloat(data.cost),
      quantityOnHand: data.quantityOnHand === '' ? null : parseFloat(data.quantityOnHand),
      weight: data.weight === '' ? null : parseFloat(data.weight),
      volume: data.volume === '' ? null : parseFloat(data.volume),
      customerLeadTime: data.customerLeadTime === '' ? null : parseInt(data.customerLeadTime, 10),
      categoryId: data.categoryId === '' ? null : parseInt(data.categoryId, 10),
      supplierId: data.supplierId === '' ? null : parseInt(data.supplierId, 10),
      brandId: data.brandId === '' ? null : parseInt(data.brandId, 10),
    };
    onSave(payload);
  };

  const renderSelect = (key, options, emptyMsg) => (
    <select
      id={key}
      value={data[key]}
      onChange={(e) => updateField(key, e.target.value)}
      disabled={options.length === 0}
    >
      {options.length === 0 ? (
        <option value="">{emptyMsg}</option>
      ) : (
        <>
          <option value="">{t('inventory.select_option_prefix')} {t(`inventory.product_form.field_${key}`)} {t('inventory.select_option_suffix')}</option>
          {options.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </>
      )}
    </select>
  );

  const renderField = (key, type = 'text', step = undefined, textarea = false) => {
    const label = t(`inventory.product_form.field_${key}`);
    const isRequired = key === 'name';
    const showError = isRequired && touched && !nameIsValid;

    if (type === 'checkbox') {
      return (
        <label htmlFor={key} key={key}>
          <input
            id={key}
            type="checkbox"
            role="switch"
            checked={!!data[key]}
            onChange={(e) => updateField(key, e.target.checked)}
          />
          {label}
        </label>
      );
    }

    return (
      <div className={`pm-form-group ${textarea || key === 'description' ? 'full-width' : ''}`} key={key}>
        <label htmlFor={key}>
          {label}
          {isRequired && <span className="pm-required"> *</span>}
        </label>
        {textarea ? (
          <textarea
            id={key}
            value={data[key]}
            onChange={(e) => updateField(key, e.target.value)}
            rows={3}
            aria-invalid={showError ? 'true' : undefined}
          />
        ) : (
          <input
            id={key}
            type={type}
            step={step}
            value={data[key]}
            onChange={(e) => updateField(key, e.target.value)}
            aria-invalid={showError ? 'true' : undefined}
          />
        )}
        {showError && (
          <span className="pm-field-error">{t('inventory.product_form.error_name_required')}</span>
        )}
      </div>
    );
  };

  const productTypeOptions = [
    { value: 'goods', label: t('inventory.product_form.type_goods') },
    { value: 'service', label: t('inventory.product_form.type_service') },
    { value: 'combo', label: t('inventory.product_form.type_combo') },
  ];

  const unitOptions = [
    { value: 'unit', label: t('inventory.product_form.unit_unit') },
    { value: 'kg', label: t('inventory.product_form.unit_kg') },
    { value: 'lb', label: t('inventory.product_form.unit_lb') },
    { value: 'liter', label: t('inventory.product_form.unit_liter') },
    { value: 'gallon', label: t('inventory.product_form.unit_gallon') },
    { value: 'meter', label: t('inventory.product_form.unit_meter') },
    { value: 'foot', label: t('inventory.product_form.unit_foot') },
    { value: 'box', label: t('inventory.product_form.unit_box') },
    { value: 'pack', label: t('inventory.product_form.unit_pack') },
  ];

  const categoryOptions = categories.map(c => ({
    value: c.id,
    label: c.name || c.categoryName,
  }));

  const supplierOptions = suppliers.map(s => ({
    value: s.id,
    label: s.name || s.supplierName,
  }));

  const brandOptions = brands.map(b => ({
    value: b.id,
    label: b.name || b.brandName,
  }));

  return (
    <div className="pm-modal-overlay">
      <div className="pm-modal-content">
        <div className="pm-modal-header">
          <h2>
            {initialData
              ? t('inventory.edit_product_title')
              : t('inventory.add_product_title')}
          </h2>
          <button className="pm-modal-close" onClick={onCancel} aria-label={t('cancel')}>
            &times;
          </button>
        </div>

        <div className="pm-modal-body">
          {error && (
            <div className="pm-error-banner">
              <span aria-hidden="true">⚠️</span>
              <span>{error}</span>
            </div>
          )}

          <div className="pm-tabs">
            {SECTIONS.map(section => (
              <button
                key={section.key}
                className={`pm-tab ${activeSection === section.key ? 'active' : ''}`}
                onClick={() => setActiveSection(section.key)}
                type="button"
              >
                <span aria-hidden="true">{section.icon}</span>
                <span>{t(`inventory.product_form.section_${section.key}`)}</span>
              </button>
            ))}
          </div>

          {activeSection === 'general' && (
            <div className="pm-section">
              <div className="pm-form-grid">
                <div className="pm-subsection-title">{t('inventory.product_form.subsection_identity')}</div>
                {renderField('name')}
                {renderField('sku')}
                {renderField('reference')}
                {renderField('barcode')}

                <div className="pm-subsection-title">{t('inventory.product_form.subsection_classification')}</div>
                <div className="pm-form-group">
                  <label htmlFor="productType">{t('inventory.product_form.field_productType')}</label>
                  {renderSelect('productType', productTypeOptions, t('inventory.product_form.no_options'))}
                </div>
                <div className="pm-form-group">
                  <label htmlFor="categoryId">{t('inventory.product_form.field_categoryId')}</label>
                  {renderSelect('categoryId', categoryOptions, t('inventory.product_form.no_categories'))}
                </div>
                <div className="pm-form-group">
                  <label htmlFor="supplierId">{t('inventory.product_form.field_supplierId')}</label>
                  {renderSelect('supplierId', supplierOptions, t('inventory.product_form.no_suppliers'))}
                </div>
                <div className="pm-form-group">
                  <label htmlFor="brandId">{t('inventory.product_form.field_brandId')}</label>
                  {renderSelect('brandId', brandOptions, t('inventory.product_form.no_brands'))}
                </div>
                <div className="pm-form-group">
                  <label htmlFor="unitOfMeasure">{t('inventory.product_form.field_unitOfMeasure')}</label>
                  {renderSelect('unitOfMeasure', unitOptions, t('inventory.product_form.no_options'))}
                </div>

                <div className="pm-subsection-title">{t('inventory.product_form.subsection_description')}</div>
                <div className="pm-form-group full-width">
                  <label htmlFor="description">{t('inventory.product_form.field_description')}</label>
                  <textarea
                    id="description"
                    value={data.description}
                    onChange={(e) => updateField('description', e.target.value)}
                    rows={3}
                  />
                </div>
              </div>
            </div>
          )}

          {activeSection === 'pricing' && (
            <div className="pm-section">
              <div className="pm-form-grid">
                <div className="pm-subsection-title">{t('inventory.product_form.subsection_pricing')}</div>
                {renderField('salesPrice', 'number', '0.01')}
                {renderField('cost', 'number', '0.01')}

                <div className="pm-subsection-title">{t('inventory.product_form.subsection_inventory_settings')}</div>
                {renderField('quantityOnHand', 'number', '1')}
                <div className="pm-toggle-row">
                  {renderField('canBeSold', 'checkbox')}
                  {renderField('trackInventory', 'checkbox')}
                  {renderField('isFavorite', 'checkbox')}
                </div>
              </div>
            </div>
          )}

          {activeSection === 'physical' && (
            <div className="pm-section">
              <div className="pm-form-grid">
                <div className="pm-subsection-title">{t('inventory.product_form.subsection_physical')}</div>
                {renderField('weight', 'number', '0.01')}
                {renderField('volume', 'number', '0.01')}
                {renderField('customerLeadTime', 'number', '1')}
                {renderField('responsible')}

                <div className="pm-subsection-title">{t('inventory.product_form.subsection_descriptions')}</div>
                <div className="pm-form-group full-width">
                  <label htmlFor="descriptionReceipts">{t('inventory.product_form.field_descriptionReceipts')}</label>
                  <textarea
                    id="descriptionReceipts"
                    value={data.descriptionReceipts}
                    onChange={(e) => updateField('descriptionReceipts', e.target.value)}
                    rows={2}
                  />
                </div>
                <div className="pm-form-group full-width">
                  <label htmlFor="descriptionDelivery">{t('inventory.product_form.field_descriptionDelivery')}</label>
                  <textarea
                    id="descriptionDelivery"
                    value={data.descriptionDelivery}
                    onChange={(e) => updateField('descriptionDelivery', e.target.value)}
                    rows={2}
                  />
                </div>
                <div className="pm-form-group full-width">
                  <label htmlFor="internalNotes">{t('inventory.product_form.field_internalNotes')}</label>
                  <textarea
                    id="internalNotes"
                    value={data.internalNotes}
                    onChange={(e) => updateField('internalNotes', e.target.value)}
                    rows={2}
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="pm-modal-footer">
          <button
            className="pm-btn-secondary"
            onClick={onCancel}
            disabled={saving}
            type="button"
          >
            {t('inventory.product_form.cancel_button')}
          </button>
          <button
            className="pm-btn-primary"
            onClick={handleSave}
            disabled={!canSave}
            aria-busy={saving ? 'true' : undefined}
            type="button"
          >
            {saving
              ? t('inventory.product_form.saving_button')
              : t('inventory.product_form.save_button')}
          </button>
        </div>
      </div>
    </div>
  );
}
